// /w/:wid/player/:uid — a player's page (yours or anyone's): avatar, name, main title, guild; rank / points / cities;
// kill score; their cities (tap → the map); titles. Others: WRITE and ATTACK; you: TITLES and SETTINGS.
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { useBack } from "@/app/useBack";
import { TitleBar } from "@/kit/chrome";
import { art } from "@/kit/art";
import { StickyBar } from "@/kit/city";
import { IconChevronRight, IconCrown, IconMail, IconShield, IconStar, IconSwords } from "@/kit/icons";
import { Bar, Button, Card, CardHead, Empty, Medallion, Row, RowText, Skeleton } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { useInit } from "@/state/queries";
import { usePlayerInfo } from "@/state/mapQueries";
import { useTitles } from "@/state/socialQueries";
import { guildOf, can, myLevel } from "@/game/guild";
import { fmtCoords, fmtNum, fmtRank, parseCoords, plural } from "@/game/format";
import { tierLine, titleName, titlesOf } from "@/game/titles";
import { EmptyBadge, TitleBadge } from "./TitleBadge";
import { TitleSheet } from "./TitleSheet";
import { VtPlayerCard } from "@/kit/VtPlayerCard";
import "../social.css";

export function PlayerProfile() {
  return (
    <WorldPage>
      <Profile />
    </WorldPage>
  );
}

function Profile() {
  const { uid: uidP } = useParams();
  const uid = Number(uidP);
  const { v, wid, vid, base } = useWorldCity();
  const nav = useNavigate();
  const session = useSession();
  const init = useInit();
  const me = uid === session.uid;
  const q = usePlayerInfo(wid, uid > 0 ? uid : null);
  const tq = useTitles(uid > 0 ? uid : null);
  const tc = useMemo(() => Object.values(q.data?.player_details ?? {})[0]?.target_char, [q.data]);
  const stats = q.data?.player_stats;
  const titles = titlesOf(tq.data?.user?.titles ?? q.data?.target_user?.titles, init.data?.titles_config?.details);
  // a title tapped (the worn one or a chip) opens big (Ben, Oct 19 2026)
  const [shown, setShown] = useState<{ title: string; tier: number } | null>(null);
  const chipTier = (name: string) => (titles.main?.title === name ? titles.main.tier : Math.max(1, ...(titles.tiers[name] ?? [])));
  const myGuild = guildOf(v);
  const sameGuild = !!myGuild && !!tc && tc.gid === myGuild.id;
  const first = tc?.villages?.[0];
  const firstXY = parseCoords(first?.coords);
  const kst = stats?.kst ?? 0, ksa = stats?.ksa ?? 0, ksd = stats?.ksd ?? 0;
  const share = (n: number) => (kst > 0 ? n / kst : 0);
  const myLv = myLevel(v, session.uid);
  // back to wherever the profile was opened from (a leaderboard row, a report, the map…), else the city (Oct 10 2026)
  const back = useBack(vid ? `${base}/city/${vid}` : base);

  if (!Number.isInteger(uid) || uid <= 0) return <Card><Empty>That isn't a player.</Empty></Card>;
  return (
    <div className="stack">
      <div className="tframe"><TitleBar title={me ? "Your profile" : "Player"} onBack={back} /></div>
      {q.isLoading && !q.data && <><Skeleton h={300} /><Skeleton h={220} /></>}
      {q.isError && <Card><Empty>Couldn't read this player. <Button size="sm" onClick={() => void q.refetch()}>Try again</Button></Empty></Card>}
      {tc && (
        <>
          <Card bare className="hero c-hero pf-hero">
            <div className="body">
              <div className="pf-herorow">
                {me ? <Medallion src={art.avatar} alt="" size={112} glow /> : <Medallion initial={tc.login} size={112} />}
                <div className="pf-herotxt">
                  <h2>{tc.login}</h2>
                  {/* the live app's wording (UserProfileDetailsScreen): private first, then the worn title, else none earned */}
                  {titles.privacy && !me ? (
                    <span className="pf-title none"><EmptyBadge size={34} />Enshrouded<small className="muted"> · in a veil of privacy</small></span>
                  ) : titles.main ? (
                    <button type="button" className="pf-title" onClick={() => setShown({ title: titles.main!.title, tier: titles.main!.tier })}><TitleBadge title={titles.main.title} tier={titles.main.tier} details={titles.details} size={34} />{titleName(titles.main.title, titles.details)}{titles.earnedCount > 1 && <small className="muted"> · +{titles.earnedCount - 1} more</small>}</button>
                  ) : titles.earnedCount > 0 ? (
                    <span className="pf-title none"><EmptyBadge size={34} />{plural(titles.earnedCount, "title")} earned · none worn</span>
                  ) : (
                    <span className="pf-title none"><EmptyBadge size={34} />Participation Trophy</span>
                  )}
                  <div className="pf-guild">
                    {tc.gid > 0 && tc.guild_info ? (
                      <button type="button" className="link" style={{ font: "inherit" }} onClick={() => nav(sameGuild ? `${base}/guild` : `${base}/rank?show=guilds`)}>
                        <b>[{tc.guild_info.tag}]</b> {tc.guild_info.name}{sameGuild && <span className="muted"> · your guild</span>}
                      </button>
                    ) : <span className="muted">No guild</span>}
                  </div>
                </div>
              </div>
            </div>
            <div className="statband">
              <div><div className="lbl">Rank</div><div className="val">{fmtRank(stats?.rank)}</div></div>
              <div><div className="lbl">Points</div><div className="val">{fmtNum(stats?.total_points ?? tc.total_points)}</div></div>
              <div><div className="lbl">Cities</div><div className="val">{fmtNum(tc.villages?.length ?? 0)}</div></div>
            </div>
          </Card>

          <div className="c-grid2" style={{ alignItems: "start" }}>
            <Card>
              <CardHead title="Kill score" />
              <div className="pf-kill">
                {/* the label over its number, so a big kill score never runs into the label (Oct 10 2026) */}
                <Row><div className="txt"><Medallion size={44}><IconStar /></Medallion><div className="ks"><span className="lbl">Total</span><span className="val">{fmtNum(kst)}</span></div></div></Row>
                <Row className="share"><div className="txt"><Medallion size={44}><IconSwords /></Medallion><div className="ks"><span className="lbl">Attacking</span><span className="val">{fmtNum(ksa)}</span></div></div><Bar value={share(ksa)} className="ks-share" /></Row>
                <Row className="share"><div className="txt"><Medallion size={44}><IconShield /></Medallion><div className="ks"><span className="lbl">Defending</span><span className="val">{fmtNum(ksd)}</span></div></div><Bar value={share(ksd)} className="ks-share" /></Row>
              </div>
            </Card>
            <Card>
              <CardHead title="Titles" aside={titles.privacy && !me ? "private" : titles.earnedCount ? `${titles.earnedCount} earned` : undefined} />
              {titles.privacy && !me ? (
                <div className="tt-private">
                  <p className="q">“Modesty can be golden, especially when there’s nothing to boast about.”</p>
                  <p className="by">Seneca</p>
                  <p className="muted">({tc.login} has chosen to keep their titles private)</p>
                </div>
              ) : titles.earned.length ? (
                <div className="chips">
                  {titles.earned.slice(0, 8).map((t) => <button type="button" key={t} className="chip" onClick={() => setShown({ title: t, tier: chipTier(t) })}><TitleBadge title={t} tier={chipTier(t)} details={titles.details} size={30} />{titleName(t, titles.details)}</button>)}
                  {titles.earned.length > 8 && <span className="chip muted">+{titles.earned.length - 8} more</span>}
                </div>
              ) : (
                <Empty>{me ? "You have not been awarded any titles (yet)." : `${tc.login} has no titles.`}</Empty>
              )}
              {me && <button type="button" className="link caps" style={{ marginTop: 12 }} onClick={() => nav(`${base}/titles`)}>Wear a title <IconChevronRight /></button>}
            </Card>
          </div>

          <VtPlayerCard wid={wid} name={tc.login} me={me} />

          <Card className="pf-cities">
            <CardHead title="Cities" aside={plural(tc.villages?.length ?? 0, "city", "cities")} />
            {!tc.villages?.length && <Empty>No cities in this world.</Empty>}
            <div className="rows">
              {(tc.villages ?? []).map((c) => {
                const xy = parseCoords(c.coords);
                return (
                  <Row key={c.id} as="button" onClick={() => (me ? nav(`${base}/city/${c.id}`) : xy ? nav(`${base}/map?x=${xy[0]}&y=${xy[1]}`) : undefined)}>
                    <Medallion src={art.map.keep(Math.max(1, Math.min(4, Number(c.level) || 1)) as 1 | 2 | 3 | 4)} alt="" size={54} />
                    <RowText line={<b className="creamt" style={{ fontWeight: 600 }}>{c.city_name}</b>} meta={`${fmtCoords(c.coords)} · R${c.continent} · ${fmtNum(c.points)} pts`} />
                    <IconChevronRight className="chev" />
                  </Row>
                );
              })}
            </div>
          </Card>

          <StickyBar>
            <div className="pf-actions">
              {me ? (
                <>
                  <Button kind="steel" size="md" onClick={() => nav(`${base}/titles`)}><IconCrown /> Titles</Button>
                  <Button kind="steel" size="md" onClick={() => nav("/settings")}>Settings</Button>
                </>
              ) : (
                <>
                  <Button kind="steel" size="md" onClick={() => nav(`${base}/mail/new?to=${encodeURIComponent(tc.login)}`)}><IconMail /> Write</Button>
                  {firstXY && vid && !sameGuild && <Button kind="crimson" size="md" onClick={() => nav(`${base}/city/${vid}/rally/commands?x=${firstXY[0]}&y=${firstXY[1]}`)}><IconSwords /> Attack</Button>}
                  {firstXY && vid && sameGuild && <Button kind="steel" size="md" className="support" onClick={() => nav(`${base}/city/${vid}/rally/commands?x=${firstXY[0]}&y=${firstXY[1]}&mode=s`)}><IconShield /> Support</Button>}
                  {myGuild && tc.gid <= 0 && can.manageInvites(myLv) && <Button kind="gold" size="md" onClick={() => nav(`${base}/guild?sub=invites`)}>Invite</Button>}
                </>
              )}
            </div>
          </StickyBar>
        </>
      )}
      <TitleSheet
        title={shown?.title ?? null}
        tier={shown?.tier}
        details={titles.details}
        onClose={() => setShown(null)}
        note={shown ? `${tierLine(titles, shown.title, shown.tier)}${titles.main?.title === shown.title && titles.main?.tier === shown.tier ? " · worn" : ""}` : undefined}
      />
    </div>
  );
}
